/**
 * FontLabelsContext
 *
 * Provides user-defined font labels to editor components.
 * Used by StyleFieldRow to show custom labels (e.g., "Main Font")
 * instead of raw font names (e.g., "SF Pro") in fontFamily dropdowns.
 *
 * When no provider exists, consumers get an empty map (= use default labels).
 *
 * @module font-config/context
 */

import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useFontConfig } from '../hooks/useFontConfig';

/**
 * Map of font name → display label.
 * Only contains entries where label differs from font name.
 */
type FontLabelsMap = Record<string, string>;

const FontLabelsContext = createContext<FontLabelsMap>({});

export interface FontLabelsProviderProps {
  children: ReactNode;
  projectId: string | undefined;
}

/**
 * Provides font labels from project's font config.
 * Wrap around editor component tree where fontFamily dropdowns need labels.
 */
export function FontLabelsProvider({ children, projectId }: FontLabelsProviderProps) {
  const { config } = useFontConfig(projectId);

  const labels = useMemo<FontLabelsMap>(
    () => config.fontLabels ?? {},
    [config.fontLabels],
  );

  return (
    <FontLabelsContext.Provider value={labels}>
      {children}
    </FontLabelsContext.Provider>
  );
}

/**
 * Get user-defined font labels map.
 * Returns empty object when no FontLabelsProvider is present.
 */
export function useFontLabels(): FontLabelsMap {
  return useContext(FontLabelsContext);
}
